import { Controller } from "@hotwired/stimulus";

const syncDelay = 500;
const reactionGroups = new WeakMap();

export default class OptimisticLikeController extends Controller {
  static targets = ["count", "icon", "link"];

  static values = {
    liked: Boolean,
    count: Number,
    likedIcon: String,
    unlikedIcon: String,
    likeUrl: String,
    unlikeUrl: String,
    reaction: String
  };

  connect() {
    const groupElement = this.element.closest(".post-option-button") || this.element;
    if (!reactionGroups.has(groupElement)) {
      reactionGroups.set(groupElement, { syncing: false, version: 0, controllers: new Set() });
    }
    this.reactionGroup = reactionGroups.get(groupElement);
    this.reactionGroup.controllers.add(this);
    this.confirmedLiked = this.likedValue;
    this.syncing = false;
    this.syncTimer = null;
    this.handlePageHide = this.flushPendingState.bind(this);
    globalThis.addEventListener("pagehide", this.handlePageHide);
  }

  toggle(event) {
    event.preventDefault();

    this.reactionGroup.version++;
    const liked = !this.likedValue;
    if (liked) this.clearOppositeReaction();
    this.renderState(liked);
    this.scheduleSync();
  }

  clearOppositeReaction() {
    for (const controller of this.reactionGroup.controllers) {
      if (controller.reactionValue !== this.reactionValue && controller.likedValue) {
        controller.renderState(false);
      }
    }
  }

  scheduleSync() {
    clearTimeout(this.syncTimer);
    this.syncTimer = null;

    if (this.syncing || this.confirmedLiked === this.likedValue) return;

    this.syncTimer = setTimeout(() => {
      this.syncTimer = null;
      this.syncState();
    }, syncDelay);
  }

  async syncState() {
    if (this.syncing || this.reactionGroup.syncing || this.confirmedLiked === this.likedValue) return;

    this.reactionGroup.syncing = true;
    const requestedVersion = this.reactionGroup.version;
    this.syncing = true;
    const requestedState = this.likedValue;

    try {
      const stream = await this.persistState(requestedState);
      this.confirmedLiked = requestedState;
      if (requestedState) {
        for (const controller of this.reactionGroup.controllers) {
          if (controller.reactionValue !== this.reactionValue) controller.confirmedLiked = false;
        }
      }

      // Do not render an older response over clicks made while it was running.
      const settled = [...this.reactionGroup.controllers].every(
        controller => controller.confirmedLiked === controller.likedValue
      );
      if (this.reactionGroup.version === requestedVersion && settled) {
        Turbo.renderStreamMessage(stream);
      }
    } catch (error) {
      if (this.reactionGroup.version === requestedVersion) {
        for (const controller of this.reactionGroup.controllers) {
          controller.renderState(controller.confirmedLiked);
        }
      }
      console.error("Unable to update like:", error);
    } finally {
      this.syncing = false;
      this.reactionGroup.syncing = false;

      for (const controller of this.reactionGroup.controllers) controller.scheduleSync();
    }
  }

  disconnect() {
    this.reactionGroup?.controllers.delete(this);
    clearTimeout(this.syncTimer);
    this.syncTimer = null;
    globalThis.removeEventListener("pagehide", this.handlePageHide);
  }

  flushPendingState() {
    clearTimeout(this.syncTimer);
    this.syncTimer = null;

    // Use the same shared request queue when leaving; requests use keepalive.
    this.syncState();
  }

  async persistState(liked) {
    const response = await fetch(
      liked ? this.likeUrlValue : this.unlikeUrlValue,
      {
        method: liked ? "POST" : "DELETE",
        headers: {
          Accept: "text/vnd.turbo-stream.html",
          "X-CSRF-Token": document.querySelector(
            "meta[name=\"csrf-token\"]"
          )?.content || "",
          "X-Requested-With": "XMLHttpRequest"
        },
        credentials: "same-origin",
        keepalive: true
      }
    );

    if (!response.ok) {
      throw new Error(`Like request failed with status ${response.status}`);
    }

    if (response.redirected) throw new Error("Like request was redirected");

    const stream = await response.text();
    if (!stream.trim()) throw new Error("Like request returned no update");

    return stream;
  }

  renderState(liked) {
    this.countValue = Math.max(
      0,
      this.countValue + (liked === this.likedValue ? 0 : liked ? 1 : -1)
    );
    this.likedValue = liked;
    this.updateElements();
  }

  updateElements() {
    for (const element of this?.countElements || []) {
      element.textContent = this.countValue;
    }

    if (this.hasIconTarget) {
      this.iconTarget.src = this.likedValue
        ? this.likedIconValue
        : this.unlikedIconValue;
    }

    this.linkTarget.setAttribute("aria-pressed", String(this.likedValue));
    this.updateTooltip();
  }

  updateTooltip() {
    const reaction = this.reactionValue === "dislike" ? "Dislike" : "Like";
    const title = this.likedValue ? `Remove ${reaction}` : reaction;
    this.element.dataset.bsOriginalTitle = title;
    this.element.dataset.translationTitleSource = title;
    delete this.element.dataset.translationTitleLanguage;
    delete this.element.dataset.translationTitleText;
    const tooltip = globalThis.bootstrap?.Tooltip.getInstance(this.element);
    if (tooltip) {
      tooltip._config.title = title;
      // Bootstrap retains setContent() overrides even while the tip is hidden.
      // Refresh that content on every toggle so the next hover uses this state.
      tooltip.setContent({ ".tooltip-inner": title });
    } else {
      this.element.setAttribute("title", title);
    }
  }

  get countElements() {
    return this.element.parentElement?.querySelectorAll(
      "[data-optimistic-like-target=\"count\"], .count-target"
    );
  }
}
